import { Delete } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { Box, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { DataGridPremium, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import {
	TimeLabel,
	Department,
	useGetAllTimeLabelsOfDepartmentLazyQuery,
	useAddLabelsToDepartmentMutation,
	useRemoveLabelsFromDepartmentMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect } from "react";


import ConformationDialog from "./Conformation";
import DialogSelectTimeLabels from "./DialogSelectTimeLabels";

export default function DialogTimeLabelsOfDepartment(
	props: Readonly<{
		department: Department;
		open: boolean;
		setOpen: (value: boolean) => void;
		refreshData: () => void;
		timeLabels: TimeLabel[];
	}>,
) {
	const { t } = useTranslation();

	const [open, setOpen] = useState(false);

	const { showNotification } = useNotification();

	const [timeLabels, setTimeLabels] = useState<TimeLabel[]>([]);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [getTimeLabels] = useGetAllTimeLabelsOfDepartmentLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data) {
				const tmp: TimeLabel[] =
					data.allTimeLabelsOfDepartment?.nodes?.map((label) => ({
						...(label as unknown as TimeLabel),
					})) ?? [];
				setTimeLabels(tmp);
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	useEffect(() => {
		setOpen(props.open);
		if (props.open && props.department.id) {
			getTimeLabels({
				variables: {
					departmentId: props.department.id,
					first: 100,
					showDeleted: false,
					onlyActive: false,
				},
			});
		}
		 
	}, [props.department.id, props.open]);

	const [addTimeLabels] = useAddLabelsToDepartmentMutation({
		onCompleted(data) {
			const res = data.addLabelsToDepartment;
			if (res.boolean) {
				showNotification(t("timetracking.timeLabels.success.timeLabelsAdded"), "success");
				props.refreshData();
				props.setOpen(false);
				setSaveClicked(false);
			} else if (res.errors) {
				const errors = res.errors;
				errors.forEach((error) => {
					if ("errors" in error && Array.isArray(error.errors)) {
						if (error.errors?.length === 0) showNotification(error.message, "error");
						else
							error.errors?.forEach((e) => {
								showNotification(e.errorMessage, "error");
							});
					} else {
						showNotification(error.message, "error");
					}
				});
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [removeTimeLabels] = useRemoveLabelsFromDepartmentMutation({
		onCompleted(data) {
			if (data.removeLabelsFromDepartment.boolean) {
				showNotification(t("timetracking.timeLabels.success.timeLabelsRemoved"), "success");
				props.refreshData();
				props.setOpen(false);
				setSaveClicked(false);
			} else if (data.removeLabelsFromDepartment.errors) {
				const errors = data.removeLabelsFromDepartment.errors;
				errors.forEach((error) => {
					if ("errors" in error && Array.isArray(error.errors)) {
						if (error.errors?.length === 0) showNotification(error.message, "error");
						else
							error.errors?.forEach((e) => {
								showNotification(e.errorMessage, "error");
							});
					} else {
						showNotification(error.message, "error");
					}
				});
				setSaveClicked(false);
			}
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const handleClose = () => {
		setOpen(false);
		props.setOpen(false);
		setSaveClicked(false);
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		event.preventDefault();
		setSaveClicked(true);

		const newTimeLabels = timeLabels.filter(
			(e: TimeLabel) => !props.timeLabels.find((c) => c.id === e.id),
		);

		const removedTimeLabels = props.timeLabels.filter(
			(e: TimeLabel) => !timeLabels.find((c) => c.id === e.id),
		);

		if (newTimeLabels.length > 0) {
			addTimeLabels({
				variables: {
					labels: newTimeLabels.map((e) => e.id),
					id: props.department.id,
				},
			});
		}
		if (removedTimeLabels.length > 0) {
			removeTimeLabels({
				variables: {
					timeLabelIds: removedTimeLabels.map((e) => e.id),
					id: props.department.id,
				},
			});
		}

		handleClose();
	};

	const columns: GridColDef<TimeLabel>[] = [
		{
			field: "name",
			headerName: t("name"),
			width: 250,
			flex: 1,
			renderCell(params: GridCellParams) {
				return params.row.displayName;
			},
		},
		{
			field: "isActive",
			headerName: t("timetracking.timeLabels.tableTimeLabel.isActive"),
			width: 150,
			renderCell: (params: GridCellParams) => {
				return params.row.isActive ? t("yes") : t("no");
			},
		},
		{
			field: "actions",
			headerName: t("actions"),
			sortable: false,
			width: 100,
			renderCell: (params: GridCellParams) => {
				const [openConformationDialog, setOpenConformationDialog] =
					 
					useState(false);
				return (
					<div
						style={{
							justifyContent: "center",
							alignItems: "center",
							display: "flex",
							padding: "7px 0",
						}}>
						<Button data-testid="delete-button" onClick={() => setOpenConformationDialog(true)}>
							<Delete />
						</Button>
						<ConformationDialog
							openPopup={openConformationDialog}
							setOpenDeleteConformation={setOpenConformationDialog}
							text={t("timetracking.timeLabels.deleteConformationTimeLabel")}
							handleYes={() => {
								if (params.row.id) {
									setTimeLabels((prev) => prev.filter((e) => e.id !== params.row.id));
								}
								setOpenConformationDialog(false);
							}}
						/>
					</div>
				);
			},
		},
	];

	const [openDialogTimeLabels, setOpenDialogTimeLabels] = useState(false);
	const [paginationModel, setPaginationModel] = useState({
		pageSize: 5,
		page: 0,
	});

	const [searchValue, setSearchValue] = useState("");
	const [filteredTimeLabels, setFilteredTimeLabels] = useState<TimeLabel[]>([]);
	const handleSearchChange = (value: string) => {
		setSearchValue(value);
		setFilteredTimeLabels(
			timeLabels.filter((label) => label.name.toLowerCase().includes(value.toLowerCase())),
		);
	};

	useEffect(() => {
		setFilteredTimeLabels(
			timeLabels.filter((label) => label.name.toLowerCase().includes(searchValue.toLowerCase())),
		);
	}, [searchValue, timeLabels]);

	return (
		<Dialog
			open={open}
			PaperProps={{
				component: "form",
				onSubmit: handleSubmit,
			}}
			maxWidth="md"
			fullWidth={true}>
			<DialogTitle>
				{t("timetracking.timeLabels.dialogTimeLabels.title")} {t("common.of")}{" "}
				{props.department.name}
				<Button
					data-testid="add-button"
					onClick={() => {
						setOpenDialogTimeLabels(true);
					}}
					className="blueButton right">
					{t("timetracking.timeLabels.dialogTimeLabels.add")}
				</Button>
				<DialogSelectTimeLabels
					open={openDialogTimeLabels}
					setOpen={setOpenDialogTimeLabels}
					timeLabels={timeLabels}
					setTimeLabels={setTimeLabels}
					contactId={props.department.companyId}
				/>
			</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						height: "auto",
						width: "100%",
						borderBottom: "1px solid lightgrey",
					}}>
					<TextField
						value={searchValue}
						onChange={(e) => handleSearchChange(e.target.value)}
						placeholder={t("dropdown.timeLabelSearch")}
						variant="outlined"
						sx={{ width: "100%", marginRight: 2, flex: 1 }}
					/>
					<DataGridPremium
						getRowId={(row: TimeLabel) => row.id}
						pagination
						rows={filteredTimeLabels}
						columns={columns}
						className="table"
						disableRowSelectionOnClick
						pageSizeOptions={[1, 2, 5]}
						paginationModel={paginationModel}
						onPaginationModelChange={setPaginationModel}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="close-button" variant="outlined" onClick={handleClose}>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					data-testid="dialog-submit-team-member"
					type="submit"
					variant="contained"
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
